import { useEffect, useState } from 'react'
import { Chip, CircularProgress, Switch } from '@mui/material'
import DataTable from '../../Components/Table/DataTable'
import { Column, ColumnType, type RowData } from '../../Components/Table/DataTable/DataTable.model'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { sendGet } from '../../hooks/use-fetch'
import { ProgramSettings } from './ProgramSettings'
import { type isdProgram } from '../../core/types'
import { useAccountId } from '../../ContextProviders/CurrentAccount'
import { ErrorProvider } from '../../ContextProviders/ErrorProvider'
import { type PaginationProps, usePagination, type SearchCriteria } from '../../hooks/use-pagination'
import { TextFieldWithLabel } from '../../Components/TextField'
import { useRefresh } from '../../ContextProviders/RefreshProvider'

const nameColumn = new Column('name', 'Name')
const fundingSourceColumn = new Column('fundingSource', 'Funding Source', ColumnType.CUSTOM)
const enabledColumn = new Column('enabled', 'Enabled?', ColumnType.CUSTOM)
const columns = [nameColumn, fundingSourceColumn, enabledColumn]

fundingSourceColumn.customCellGenerator = row => {
  if (row.fundingSource === null || row.fundingSource === undefined) { return <></> }
  const colorItem = row.fundingSource?.id % 3
  let indicatorClass = 'cyan-'
  if (colorItem === 1) {
    indicatorClass = 'purple-'
  } else if (colorItem === 2) {
    indicatorClass = 'red-'
  }
  return <Chip icon={<FiberManualRecordIcon className={indicatorClass + 'icon'} />} label={row.fundingSource?.name} size='small' className={indicatorClass + 'indicator'} />
}

export function ProgramISDList (): JSX.Element {
  const isdID = useAccountId()?.id ?? 0
  const [priorityId, setPriorityId] = useState<number | null>(null)
  const [loaded, setLoaded] = useState(false)
  const refreshTime = useRefresh()
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [currentProgram, setCurrentProgram] = useState<null | isdProgram>(null)
  const [rows, setRows] = useState<isdProgram[]>([])
  const [searchValue, setSearchValue] = useState(null)

  const paginationProps: PaginationProps<RowData> = {
    rows
  }
  const pagination = usePagination(paginationProps)

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response: sd } = await sendGet(`/ISDPrograms/GetPrograms?ISDID=${isdID}`, {})
      const progs = sd as isdProgram[]
      const prioProgs = progs.filter(p => p.priorityProgram)
      const prioProgId = prioProgs.length > 0 ? prioProgs[0].id : null
      setPriorityId(prioProgId)
      setRows(sd)
      setLoaded(true)
    }
    void loadData()
  }, [refreshTime, isdID])

  enabledColumn.customCellGenerator = row => {
    return <Switch
      size='medium'
      value={row.programID} checked={row.enabled ?? false}
      onChange={() => {}}
      data-testid={`switch-${row.id as number}`}
    />
  }

  function documentClicked (program: isdProgram): void {
    setCurrentProgram(program)
    setIsDetailModalOpen(true)
  }

  const handleClose = (): void => {
    setIsDetailModalOpen(false)
  }

  useEffect(() => {
    const fieldArray: SearchCriteria[] = []
    if (searchValue !== null) {
      fieldArray.push({
        field: 'name',
        value: searchValue
      })
    }
    pagination.setSearchFields(fieldArray)
  }, [searchValue])

  if (!loaded) return <CircularProgress />

  return (<>
    <TextFieldWithLabel
      label='Name'
      onChange={(e) => { setSearchValue(e.target.value) }}
      name='name'
      maxWidth='205px'
      value={searchValue ?? ''}
      data-testid="name-search"
    />
    <DataTable
      page={pagination.page}
      name='programTable'
      hasCheckbox={false}
      columns={columns}
      rows={pagination.internalRows}
      totalRecords={pagination.recordCount}
      loading={false}
      onSortChange={(col, sorts) => {
        pagination.onSortChange(col, sorts[0].order)
      }}
      onRowClick={(evt, row) => {
        documentClicked(row as isdProgram)
      }}
      onPageChange={pagination.handleChangePage}
      onRowsPerPageChange={pagination.handleChangeRowsPerPage}
    />

    {isDetailModalOpen && currentProgram != null && (
      <ErrorProvider>
        <ProgramSettings
          open={isDetailModalOpen}
          isdID={isdID}
          program={currentProgram}
          onClose={handleClose}
          priorityId={priorityId}
        />
      </ErrorProvider>
    )}
  </>)
}
